/**
 * EmbeddedComplianceGate
 *
 * Собирает compliance-подтверждение у владельца организации в embedded-
 * приложении (Bitrix24). Пока подтверждения нет, ingest отбрасывает ВСЕ записи
 * организации (IngestWorker: reason consent_not_confirmed) — то есть без этого
 * экрана установка выглядит рабочей, а звонки молча теряются.
 *
 * Почему отдельный гейт, а не шаг визарда: организации, установившие
 * приложение раньше, онбординг-опрос уже прошли (onboardingSurveyCompleted =
 * true) и второй раз его не увидят. Гейт смотрит только на complianceConfirmed,
 * поэтому одинаково покрывает свежие и существующие установки.
 *
 * Показывается ТОЛЬКО владельцу (role 'client'): подтверждение юридическое,
 * рядовой сотрудник его давать не может. Все прочие роли проходят насквозь —
 * серверный гейт в ingest всё равно остаётся.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { LanguageToggle } from '@/components/theme/LanguageToggle';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { API_BASE_URL } from '@/lib/api/config';
import {
  awaitEmbeddedRecoveryOutcome,
  notifyEmbeddedSessionExpired,
} from '@/lib/embedded-session-recovery';
import { embeddedGateHeightClass, embeddedSessionAuthHeaders } from '@/lib/embedded-host';

interface EmbeddedComplianceGateProps {
  children: ReactNode;
}

const CONSENT_MODE_VALUES = [
  'all_party',
  'one_party',
  'employer_notification',
  'other_lawful_basis',
] as const;
const JURISDICTION_VALUES = [
  'ua',
  'eu_strict',
  'eu_moderate',
  'us_all_party',
  'us_one_party',
  'custom',
] as const;
const LEGAL_BASIS_VALUES = [
  'consent',
  'legitimate_interest',
  'contract',
  'legal_obligation',
  'other_applicable_law',
] as const;

export function EmbeddedComplianceGate({ children }: EmbeddedComplianceGateProps) {
  const { t } = useTranslation(['onboarding', 'common']);
  const { role, complianceConfirmed, csrfToken, isLoading, refreshSession, crmVendor } =
    useEmbeddedOrg();

  // Compliance is an explicit declaration: do not preselect legally meaningful
  // answers that the owner could submit without reviewing.
  const [consentMode, setConsentMode] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [legalBasis, setLegalBasis] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isComplete = Boolean(consentMode && jurisdiction && legalBasis);

  const handleSubmit = async () => {
    if (!csrfToken || !accepted || !isComplete) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/v1/embedded/consent/compliance`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          ...embeddedSessionAuthHeaders(),
        },
        body: JSON.stringify({ consentMode, jurisdiction, legalBasis }),
      });

      // A gate left open past the session's hour answers 401: the same
      // recovery as every other embedded request (a Bitrix24 re-entry, or
      // `aelo:session-expired` to a message host), not a save error the user
      // can only retry into the same 401.
      if (
        res.status === 401 &&
        notifyEmbeddedSessionExpired() &&
        (await awaitEmbeddedRecoveryOutcome())
      ) {
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Перечитываем сессию: complianceConfirmed приходит оттуда, и без
      // refresh гейт остался бы на экране после успешного подтверждения.
      await refreshSession();
    } catch (err) {
      console.error('embedded_compliance_confirm_failed', err);
      setError(t('compliance.failedToSave', 'Failed to save. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Сессия ещё читается — соседние гейты уже показывают свой спиннер.
  if (isLoading || complianceConfirmed === null) {
    return <>{children}</>;
  }

  if (complianceConfirmed || role !== 'client') {
    return <>{children}</>;
  }

  return (
    <main
      aria-labelledby="embedded-compliance-title"
      className={`flex ${embeddedGateHeightClass(crmVendor, 'min-h-screen')} items-center justify-center overflow-y-auto bg-bg-void p-4`}
    >
      <div className="w-full max-w-lg">
        <div className="mb-4 flex justify-end">
          <LanguageToggle showLabel />
        </div>

        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-dim">
            <ShieldCheck className="h-8 w-8 text-primary" />
          </div>
          <h2
            id="embedded-compliance-title"
            className="mb-2 text-2xl font-semibold text-fg"
          >
            {t('compliance.title', 'Compliance settings')}
          </h2>
          <p className="text-sm text-fg-muted">
            {t(
              'compliance.description',
              'Before Aelo can process recordings, provide your organization’s recording approach, jurisdiction, and legal basis.'
            )}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
          className="space-y-5"
        >
          <div className="space-y-1.5">
            <label
              htmlFor="embedded-consent-mode"
              className="text-sm font-medium text-fg"
            >
              {t('compliance.consentMode', 'Recording and notification approach')}
            </label>
            <Select
              id="embedded-consent-mode"
              data-testid="embedded-compliance-consent-mode"
              value={consentMode}
              onValueChange={(val) => setConsentMode(val as string)}
              disabled={isSubmitting}
              className="w-full"
              placeholder={t('compliance.selectConsentMode', 'Select an approach')}
              options={CONSENT_MODE_VALUES.map((value) => ({
                value,
                label: t(`compliance.consentModes.${value}`),
                description: t(`compliance.consentModeDescriptions.${value}`),
              }))}
            />
            <p className="text-xs text-fg-muted">
              {t(
                'compliance.consentModeHelp',
                'These are operational descriptions, not terms from a specific statute. Choose the option that matches your organization’s practice.'
              )}
            </p>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="embedded-jurisdiction"
              className="text-sm font-medium text-fg"
            >
              {t('compliance.jurisdiction', 'Jurisdiction')}
            </label>
            <Select
              id="embedded-jurisdiction"
              data-testid="embedded-compliance-jurisdiction"
              value={jurisdiction}
              onValueChange={(val) => setJurisdiction(val as string)}
              disabled={isSubmitting}
              className="w-full"
              placeholder={t('compliance.selectJurisdiction', 'Select a jurisdiction')}
              options={JURISDICTION_VALUES.map((value) => ({
                value,
                label: t(`compliance.jurisdictions.${value}`),
              }))}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="embedded-legal-basis" className="text-sm font-medium text-fg">
              {t('compliance.legalBasis', 'Legal basis')}
            </label>
            <Select
              id="embedded-legal-basis"
              data-testid="embedded-compliance-legal-basis"
              value={legalBasis}
              onValueChange={(val) => setLegalBasis(val as string)}
              disabled={isSubmitting}
              className="w-full"
              placeholder={t('compliance.selectLegalBasis', 'Select a legal basis')}
              options={LEGAL_BASIS_VALUES.map((value) => ({
                value,
                label: t(`compliance.legalBases.${value}`),
              }))}
            />
            <p className="text-xs text-fg-muted">
              {t(
                'compliance.legalBasisHelp',
                'Choose the basis that applies to your organization under the selected jurisdiction.'
              )}
            </p>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="compliance-accept"
              data-testid="embedded-compliance-accept"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
            />
            <label htmlFor="compliance-accept" className="cursor-pointer text-sm text-fg">
              {t(
                'compliance.confirmation',
                'I understand that my organization, as controller, is responsible for determining and documenting an applicable legal basis and for providing notices or obtaining consent where required by applicable law.'
              )}
            </label>
          </div>

          <div role="alert" aria-live="assertive" aria-atomic="true">
            {error && (
              <div className="rounded-lg border border-danger bg-danger-dim p-3">
                <p className="text-sm text-danger-fg">{error}</p>
              </div>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            data-testid="embedded-compliance-submit"
            disabled={!accepted || !isComplete || isSubmitting}
            aria-busy={isSubmitting}
            className="w-full gap-2"
          >
            {isSubmitting && (
              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
            )}
            {isSubmitting
              ? t('common:common.saving', 'Saving...')
              : t('compliance.submit', 'Confirm and continue')}
          </Button>
        </form>
      </div>
    </main>
  );
}
